import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../../supabaseClient.js";
import "../../styles/ReceiveWax.css";
import "../../styles/print.css";

const ReceiveWax = () => {
  const [lots, setLots] = useState([]);
  const [popupItem, setPopupItem] = useState(null);

  const scrollRef = useRef({});
  const highlightLotNo = new URLSearchParams(window.location.search).get("lot");

  useEffect(() => {
    fetchLots();
  }, []);

  useEffect(() => {
    if (highlightLotNo && lots.length > 0) {
      const target = lots.find((l) => l.lot_no === highlightLotNo);
      if (target && scrollRef.current[target.id]) {
        scrollRef.current[target.id].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        scrollRef.current[target.id].classList.add("highlight-lot");

        setTimeout(() => {
          scrollRef.current[target.id]?.classList.remove("highlight-lot");
        }, 4000);
      }
    }
  }, [lots, highlightLotNo]);

  const fetchLots = async () => {
    const { data: lotsData } = await supabase
      .from("WAX_LOTS")
      .select("id, lot_no, worker_id, issue_date, status")
      .eq("status", "ISSUED")
      .order("issue_date", { ascending: false });

    if (!lotsData) return;

    const lotIds = lotsData.map((l) => l.id);

    const { data: items } = await supabase
      .from("WAX_ITEMS")
      .select(`
        id,
        lot_id,
        item_id,
        design_code,
        gross_weight,
        boxes_count,
        per_box_weight,
        total_box_weight,
        bags_count,
        per_bag_weight,
        total_bag_weight,
        net_weight,
        pieces_count,
        item_name:ITEMS(item_name)
      `)
      .in("lot_id", lotIds);

    const { data: received } = await supabase
      .from("WAX_RECEIVE_ITEMS")
      .select("item_id");

    const receivedSet = new Set(received?.map((r) => r.item_id));

    const merged = lotsData.map((lot) => ({
      ...lot,
      items: items
        .filter((i) => i.lot_id === lot.id)
        .map((i) => ({ ...i, is_received: receivedSet.has(i.id) })),
    }));

    setLots(merged);
  };

  /* MOVE TO CASTING BUTTON LOGIC (ADDED BACK) */
  const moveToCasting = async (lotId) => {
    await supabase
      .from("WAX_LOTS")
      .update({ status: "COMPLETED" }) // old behavior restored
      .eq("id", lotId);

    alert("Entire lot moved to Casting!");
    fetchLots();
  };

  /* ============================================================
      PRINT FIX — TABLE = 60% LEFT ALIGNED + SMALL QR
  ============================================================ */
  const printLot = async (lot) => {
    const { data: items } = await supabase
      .from("WAX_ITEMS")
      .select(`
        design_code,
        gross_weight,
        net_weight,
        pieces_count,
        boxes_count,
        per_box_weight,
        total_box_weight,
        bags_count,
        per_bag_weight,
        total_bag_weight,
        item_name:ITEMS(item_name)
      `)
      .eq("lot_id", lot.id);

    const win = window.open("", "_blank", "width=900,height=800");

    win.document.write(`
      <html>
      <head>
        <title>Print Lot</title>

        <style>
          body { 
            font-family: Arial; 
            padding: 20px; 
          }

          table { 
            width: 60%; 
            border-collapse: collapse; 
            font-size: 14px; 
          }

          th, td { 
            padding: 6px; 
            border: 1px solid #000; 
            vertical-align: top; 
          }

          th { 
            background: #f2f2f2; 
            font-weight: bold; 
          }

          #qrCanvas {
            width: 100px !important;
            height: 100px !important;
          }
        </style>

        <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
      </head>

      <body>

        <h2>Lot: ${lot.lot_no}</h2>
        <h3>Date: ${lot.issue_date.slice(0, 10)}</h3>

        <canvas id="qrCanvas"></canvas>
        
        <table>
          <tr><th>#</th><th>Details</th></tr>

          ${items.map(
            (i, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>
                <b>${i.item_name.item_name}</b> (${i.design_code})<br>
                Gross: ${i.gross_weight}g | Net: ${i.net_weight}g | Pcs: ${i.pieces_count}<br>
                Boxes: ${i.boxes_count} × ${i.per_box_weight} = ${i.total_box_weight}<br>
                Bags: ${i.bags_count} × ${i.per_bag_weight} = ${i.total_bag_weight}
              </td>
            </tr>`
          ).join("")}

        </table>

        <script>
          new QRious({
            element: document.getElementById("qrCanvas"),
            value: "${lot.lot_no}",
            size: 100,
            level: "H"
          });

          window.onload = () => { 
            window.print(); 
            window.close(); 
          };
        </script>

      </body>
      </html>
    `);

    win.document.close();
  };

  /* ============================================================
        RECEIVE POPUP (FULL VERSION)
  ============================================================ */
  const ReceivePopup = ({ item, lot, onClose }) => {
    const [itemName, setItemName] = useState("");

    useEffect(() => {
      loadName();
    }, []);

    const loadName = async () => {
      const { data } = await supabase
        .from("ITEMS")
        .select("item_name")
        .eq("id", item.item_id)
        .single();
      if (data) setItemName(data.item_name);
    };

    const [useSame, setUseSame] = useState(true);
    const [receivedGross, setReceivedGross] = useState("");
    const [boxes, setBoxes] = useState(item.boxes_count);
    const [perBox, setPerBox] = useState(item.per_box_weight);
    const [bags, setBags] = useState(item.bags_count);
    const [perBag, setPerBag] = useState(item.per_bag_weight);

    const [rejected, setRejected] = useState(0);
    const [micro, setMicro] = useState(0);
    const [big, setBig] = useState(0);

    const safePcs = Number(item.pieces_count) > 0 ? Number(item.pieces_count) : 0;
    const avg = safePcs === 0 ? 0 : Number(item.net_weight) / safePcs;
    const rejectedWeight = safePcs === 0 ? 0 : +(rejected * avg).toFixed(3);

    const totalBoxWeight = (boxes || 0) * (perBox || 0);
    const totalBagWeight = (bags || 0) * (perBag || 0);

    const receivedNet =
      receivedGross === ""
        ? 0
        : +(
            Number(receivedGross || 0) - totalBoxWeight - totalBagWeight
          ).toFixed(3);

    const expectedNet =
      safePcs === 0
        ? Number(item.net_weight)
        : +(Number(item.net_weight) - rejectedWeight).toFixed(3);

    const gainLoss = +(receivedNet - expectedNet).toFixed(3);

    const saveReceive = async () => {
      await supabase.from("WAX_RECEIVE_ITEMS").insert([
        {
          lot_id: lot.id,
          item_id: item.id,
          received_boxes_count: boxes,
          received_per_box_weight: perBox,
          received_total_box_weight: totalBoxWeight,
          received_bags_count: bags,
          received_per_bag_weight: perBag,
          received_total_bag_weight: totalBagWeight,
          rejected_pieces: rejected,
          received_pieces_count: safePcs - rejected,
          received_net_weight: receivedNet,
          micro_stone: micro,
          big_stone: big,
          gain_loss: gainLoss,
        },
      ]);

      await supabase
        .from("WAX_ITEMS")
        .update({
          received_gross: receivedGross,
          received_net: receivedNet,
          received_pcs: safePcs - rejected,
        })
        .eq("id", item.id);

      alert("Item Received Successfully!");
      onClose();
      fetchLots();
    };

    return (
      <div className="popup-overlay">
        <div className="popup-box">

          <h2 className="popup-title">Receive Item – {lot.lot_no}</h2>

          <p><b>Item:</b> {itemName}</p>
          <p><b>Design:</b> {item.design_code}</p>

          <h3>Issued Details</h3>
          <p>Gross: {item.gross_weight} | Net: {item.net_weight} | Pcs: {item.pieces_count} | Avg: {avg.toFixed(3)}</p>

          <h3>Receiving Details</h3>

          <label>
            <input
              type="checkbox"
              checked={useSame}
              onChange={() => {
                setUseSame(!useSame);
                if (!useSame) {
                  setBoxes(item.boxes_count);
                  setPerBox(item.per_box_weight);
                  setBags(item.bags_count);
                  setPerBag(item.per_bag_weight);
                }
              }}
            />
            Use issued values
          </label>

          <div className="popup-grid">
            <div>
              <label>Received Gross</label>
              <input
                type="number"
                value={receivedGross}
                onChange={(e) => setReceivedGross(e.target.value)}
              />
            </div>

            <div>
              <label>Boxes</label>
              <input
                disabled={useSame}
                type="number"
                value={boxes}
                onChange={(e) => setBoxes(+e.target.value)}
              />
            </div>

            <div>
              <label>Wt/Box</label>
              <input
                disabled={useSame}
                type="number"
                value={perBox}
                onChange={(e) => setPerBox(+e.target.value)}
              />
            </div>

            <div>
              <label>Bags</label>
              <input
                disabled={useSame}
                type="number"
                value={bags}
                onChange={(e) => setBags(+e.target.value)}
              />
            </div>

            <div>
              <label>Wt/Bag</label>
              <input
                disabled={useSame}
                type="number"
                value={perBag}
                onChange={(e) => setPerBag(+e.target.value)}
              />
            </div>
          </div>

          <h3>Rejected & Stones</h3>

          <div className="popup-grid">
            <div>
              <label>Rejected Pcs</label>
              <input
                type="number"
                value={rejected}
                onChange={(e) => setRejected(+e.target.value)}
              />
            </div>

            <div>
              <label>Micro Stone</label>
              <input
                type="number"
                value={micro}
                onChange={(e) => setMicro(+e.target.value)}
              />
            </div>

            <div>
              <label>Big Stone</label>
              <input
                type="number"
                value={big}
                onChange={(e) => setBig(+e.target.value)}
              />
            </div>
          </div>

          <h3>Calculated</h3>
          <p>Rejected Wt: {rejectedWeight}</p>
          <p>Expected Net: {expectedNet}</p>
          <p>Received Net: {receivedNet}</p>
          <p>Gain/Loss: {gainLoss}</p>

          <div className="popup-buttons">
            <button className="wax-btn" onClick={saveReceive}>Save Receive</button>
            <button className="wax-btn" onClick={onClose}>Close</button>
          </div>

        </div>
      </div>
    );
  };

  /* ============================================================
        MAIN RENDER
  ============================================================ */
  return (
    <div className="receive-container">

      <h2 className="page-title">Receive Wax</h2>

      {lots.map((lot) => (
        <div key={lot.id} className="lot-card" ref={(el) => (scrollRef.current[lot.id] = el)}>
          <div className="lot-header">
            <h3>{lot.lot_no}</h3>
            <p>{lot.issue_date}</p>

            <div className="lot-top-buttons">
              <button
                className="wax-btn"
                onClick={() =>
                  (window.location.href =
                    `/wax-issue?continueLot=${lot.lot_no}&worker=${lot.worker_id}`)
                }
              >
                + Add Items
              </button>

              <button className="wax-btn" onClick={() => printLot(lot)}>
                Print
              </button>
            </div>

            {/* ADDED BACK — SHOW MOVE TO CASTING ONLY IF ALL ITEMS COMPLETED */}
            {lot.items.length > 0 &&
              lot.items.every((i) => i.is_received) && (
                <button
                  className="wax-btn"
                  style={{ marginTop: "10px" }}
                  onClick={() => moveToCasting(lot.id)}
                >
                  Move to Casting
                </button>
              )}
          </div>

          <table className="wax-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Design</th>
                <th>Gross</th>
                <th>Net</th>
                <th>Pcs</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {lot.items.map((i) => (
                <tr key={i.id}>
                  <td>{i.item_name?.item_name}</td>
                  <td>{i.design_code}</td>
                  <td>{i.gross_weight}</td>
                  <td>{i.net_weight}</td>
                  <td>{i.pieces_count}</td>
                  <td>
                    {i.is_received ? (
                      <span className="green">Completed</span>
                    ) : (
                      <span className="red">Pending</span>
                    )}
                  </td>
                  <td>
                    {!i.is_received && (
                      <button className="wax-btn" onClick={() => setPopupItem({ lot, item: i })}>
                        Receive
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {popupItem && (
        <ReceivePopup
          lot={popupItem.lot}
          item={popupItem.item}
          onClose={() => setPopupItem(null)}
        />
      )}

    </div>
  );
};

export default ReceiveWax;
